/**
 * Licence options offered on the COACH profile.
 *
 * WHY THIS FILE EXISTS
 * --------------------
 * The licence dropdown was never one shared list. `CoachProfile.js` and
 * `components/profile/StaffProfileForm.js` have always carried two independent
 * copies of the same array. Because they were copies, the coach copy drifted
 * into being a catalogue of every certificate the club world knows — medical
 * doctors, physiotherapists, sports psychologists, nutritionists and the
 * analyst tools Wyscout / InStat / Hudl / Opta — while the one licence a head
 * coach actually holds, UEFA Pro, was missing entirely. A coach could only
 * record it through "Other" plus free text.
 *
 * This file is the coach half of that list, split off and corrected. The staff
 * half deliberately stays where it is, inline in StaffProfileForm.js and
 * unchanged: a physiotherapist certificate is right for a physiotherapist, and
 * nothing here should move it.
 *
 * WHAT BELONGS HERE
 * -----------------
 * The scope is set by the code, not by taste: a coach profile can only hold a
 * role from `COACH_ROLE_OPTIONS` (constants/staffRoles.js) — Head Coach,
 * Manager, First Team Coach, Assistant Coach, Goalkeeping Coach, Strength and
 * Conditioning Coach, Youth Coach. None of those is a doctor, a psychologist,
 * a nutritionist or a video analyst, which is exactly why those certificates
 * are gone from here. The goalkeeping and fitness rungs stay, because
 * Goalkeeping Coach and Strength and Conditioning Coach are on that list.
 *
 * NAMING
 * ------
 * The labels are the strings the platform already used — "UEFA A", "UEFA B",
 * "UEFA C", "UEFA GK Level n", "UEFA Fitness" — reused verbatim so their
 * existing entries in `translations/pages/profile/coach-profile.json` keep
 * matching. Only "UEFA Pro" is new, and it is spelled to sit in that same
 * ladder. UEFA licence designations are proper names and stay English in all
 * 16 locales.
 *
 * STORAGE
 * -------
 * `AddLicenseModal` saves the LABEL, not the value, and the backend takes it
 * verbatim: `CoachLicense.license_type` is a plain `CharField(max_length=255)`
 * with no `choices` and no serializer validation. There is no backend enum to
 * follow — this list is the only definition there is. It also means every
 * licence a coach already stored by hand through "Other" stays readable: the
 * profile renders `license.type` as-is and never looks it up here.
 *
 * `category` is documentation only. StaffProfileDropdown renders a flat list
 * and never reads it.
 */
export const COACH_LICENSE_OPTIONS = [
  // The UEFA coaching ladder, highest first.
  { value: "uefa_pro", label: "UEFA Pro", category: "coaches" },
  { value: "uefa_a", label: "UEFA A", category: "coaches" },
  { value: "uefa_b", label: "UEFA B", category: "coaches" },
  { value: "uefa_c", label: "UEFA C", category: "coaches" },
  { value: "national_fa", label: "National FA licenses", category: "coaches" },

  // Goalkeeping coaches.
  { value: "uefa_gk_1", label: "UEFA GK Level 1", category: "goalkeeper_coaches" },
  { value: "uefa_gk_2", label: "UEFA GK Level 2", category: "goalkeeper_coaches" },
  { value: "uefa_gk_3", label: "UEFA GK Level 3", category: "goalkeeper_coaches" },

  // Fitness / strength and conditioning coaches.
  { value: "uefa_fitness", label: "UEFA Fitness", category: "fitness_coaches" },
  { value: "s_c", label: "S&C", category: "fitness_coaches" },
  { value: "nsca_cscs", label: "NSCA-CSCS", category: "fitness_coaches" },

  // Anything outside the ladder keeps its free-text escape hatch.
  { value: "other", label: "Other", category: "other" },
];

export default COACH_LICENSE_OPTIONS;
